import { c as _c } from "react/compiler-runtime";
import Badge from '@precis-digital/kurama/Badge';
import Button from '@precis-digital/kurama/Button';
import Divider from '@precis-digital/kurama/Divider';
import Grid from '@precis-digital/kurama/Grid';
import { LineChart } from '@precis-digital/kurama/charts/LineChart';
import MultiSelectDropdown, { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import SingleSelectDropdown from '@precis-digital/kurama/SingleSelectDropdown';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import Toggle from '@precis-digital/kurama/Toggle';
import Tooltip from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import ThemedImage from 'shared/components/Themed/Image';
import toolsIcon from 'public/assets/images/tools.svg';
import { last } from 'ramda';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Block from 'shared/components/Block';
import ChartLegend from 'shared/components/ChartLegend';
import { ChartToolTip } from 'shared/components/ChartToolTip';
import { makeErrorToast } from 'shared/components/Toaster';
import { useCurrentClient } from 'shared/context/ClientContext';
import { getDifferenceInDays } from 'shared/dateFns';
import { getFormattedValue } from 'shared/numberFormat';
import { useTranslation } from 'shared/translations';
import { generateCompleteDateRange, snakeCaseToTitleCase, sortOptionInAlphabeticalOrder } from 'shared/utils';
import { type HomeProgressOverTimeChartQueryState, useHomeProgressOverTimeChartQueryState } from 'src/home/hooks/useHomeProgressOverTimeChartQueryState';
import { type Metric } from 'src/home/reportTemplateParameters';
import { type Report, transformToFilters, type TransformToTimeSeriesByMetricAndBreakdownFn } from 'src/home/transformations';
import { fillMissingLineChartData, formatTooltipDescriptionByDataKey, getChartLegendData, getPartsFromDataKey, getProgressOverTimeLineConfigs, type TimePeriodType } from './utils';
interface ProgressOverTimeRecord {
  fullDate: string;
  x: string;
  [key: string]: number | string;
}
export type ProgressOverTimeRecords = ProgressOverTimeRecord[];
const MINIMUM_METRICS = 1;
const MAXIMUM_METRICS = 2;
const MINIMUM_BREAKDOWN = 0;
const MAXIMUM_BREAKDOWN = 6;
const Y_AXIS_LABEL_OFFSET = 6.5;
interface ProgressOverTimeProps<TReport extends Report, TMetric extends Metric> {
  reports: TReport[];
  reportsForPreviousTimePeriod: TReport[];
  timePeriod: [string, string];
  selectedPeriodType?: TimePeriodType;
  metrics: readonly TMetric[];
  errorBody?: React.ReactNode;
  transformToTimeSeriesByMetricAndBreakdown?: TransformToTimeSeriesByMetricAndBreakdownFn<TReport, TMetric>;
  isBudgetMode: boolean;
  chartQueryState?: HomeProgressOverTimeChartQueryState<TMetric>;
  ref?: React.RefObject<HTMLDivElement | null>;
}
export default function ProgressOverTime(t0) {
  const $ = _c(81);
  const {
    reports,
    reportsForPreviousTimePeriod,
    timePeriod,
    metrics,
    errorBody,
    selectedPeriodType: t1,
    transformToTimeSeriesByMetricAndBreakdown,
    isBudgetMode,
    chartQueryState: chartQueryStateProp,
    ref: refProp
  } = t0;
  const selectedPeriodType = t1 === undefined ? "previous_period" : t1;
  const theme = useTheme();
  const {
    t
  } = useTranslation("home");
  const internalRef = useRef(null);
  const internalChartQueryState = useHomeProgressOverTimeChartQueryState();
  const chartQueryState = chartQueryStateProp ?? internalChartQueryState;
  const ref = refProp ?? internalRef;
  const {
    currentClient: t2
  } = useCurrentClient();
  const {
    currency: targetCurrencyCode
  } = t2;
  const {
    metrics: userSelectedMetrics,
    setMetrics: setUserSelectedMetrics,
    channels: userSelectedChannels,
    setChannels: setUserSelectedChannels,
    markets: userSelectedMarkets,
    setMarkets: setUserSelectedMarkets,
    aggregation: aggregationSetting,
    setAggregation: setAggregationSetting,
    isComparisonMode,
    setIsComparisonMode
  } = chartQueryState;
  const finalizedMetrics = userSelectedMetrics;
  const finalizedChannels = userSelectedChannels;
  const finalizedMarkets = userSelectedMarkets;
  const isCumulativeTs = aggregationSetting === "cumulative";
  let t3;
  if ($[0] !== userSelectedChannels || $[1] !== userSelectedMarkets) {
    t3 = [...userSelectedChannels, ...userSelectedMarkets];
    $[0] = userSelectedChannels;
    $[1] = userSelectedMarkets;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const userSelectedBreakdown = t3;
  let t4;
  if ($[3] !== finalizedChannels || $[4] !== finalizedMarkets) {
    t4 = [...finalizedChannels, ...finalizedMarkets];
    $[3] = finalizedChannels;
    $[4] = finalizedMarkets;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const selectedBreakdown = t4;
  let t5;
  bb0: {
    if (reports.length === 0 || transformToTimeSeriesByMetricAndBreakdown == null) {
      let t6;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[6] = t6;
      } else {
        t6 = $[6];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[7] !== finalizedChannels || $[8] !== finalizedMarkets || $[9] !== finalizedMetrics || $[10] !== isBudgetMode || $[11] !== isComparisonMode || $[12] !== isCumulativeTs || $[13] !== reports || $[14] !== reportsForPreviousTimePeriod || $[15] !== selectedBreakdown.length || $[16] !== selectedPeriodType || $[17] !== timePeriod || $[18] !== transformToTimeSeriesByMetricAndBreakdown) {
      t6 = transformToTimeSeriesByMetricAndBreakdown({
        records: reports,
        previousRecords: reportsForPreviousTimePeriod,
        metrics: finalizedMetrics,
        daysRange: getDifferenceInDays(...timePeriod),
        markets: finalizedMarkets.map(_temp),
        channels: finalizedChannels.map(_temp2),
        isComparisonMode: selectedBreakdown.length <= 2 && isComparisonMode,
        selectedPeriodType,
        isBudgetMode,
        isCumulativeMode: isCumulativeTs
      });
      $[7] = finalizedChannels;
      $[8] = finalizedMarkets;
      $[9] = finalizedMetrics;
      $[10] = isBudgetMode;
      $[11] = isComparisonMode;
      $[12] = isCumulativeTs;
      $[13] = reports;
      $[14] = reportsForPreviousTimePeriod;
      $[15] = selectedBreakdown.length;
      $[16] = selectedPeriodType;
      $[17] = timePeriod;
      $[18] = transformToTimeSeriesByMetricAndBreakdown;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    const tsData = t6;
    t5 = tsData;
  }
  const progressOverTime = t5;
  const lineConfigs = getProgressOverTimeLineConfigs(progressOverTime, theme);
  let marketOptions;
  let t6;
  if ($[20] !== reports || $[21] !== t || $[22] !== userSelectedBreakdown || $[23] !== userSelectedChannels || $[24] !== userSelectedMarkets) {
    const {
      channels,
      markets
    } = transformToFilters(reports);
    marketOptions = Array.from(markets).map(market => {
      const hasAtLeastOneChannelSelected = userSelectedChannels.length > MINIMUM_BREAKDOWN;
      const hasMaximumBreakdown = userSelectedBreakdown.length === MAXIMUM_BREAKDOWN;
      const isNotCurrentlySelected = userSelectedMarkets.find(userSelectedMarket => userSelectedMarket.value === market.id) == null;
      return {
        label: market.name,
        value: market.id,
        subCategory: t("markets"),
        disabled: hasAtLeastOneChannelSelected || hasMaximumBreakdown && isNotCurrentlySelected
      };
    }).sort(sortOptionInAlphabeticalOrder);
    t6 = Array.from(channels).map(channel => {
      const hasAtLeastOneMarketSelected = userSelectedMarkets.length > 0;
      const hasMaximumBreakdown_0 = userSelectedBreakdown.length === MAXIMUM_BREAKDOWN;
      const isNotCurrentlySelected_0 = userSelectedChannels.find(userSelectedChannel => userSelectedChannel.value === channel.id) == null;
      return {
        label: channel.name,
        value: channel.id,
        subCategory: t("channels"),
        disabled: hasAtLeastOneMarketSelected || hasMaximumBreakdown_0 && isNotCurrentlySelected_0
      };
    }).sort(sortOptionInAlphabeticalOrder);
    $[20] = reports;
    $[21] = t;
    $[22] = userSelectedBreakdown;
    $[23] = userSelectedChannels;
    $[24] = userSelectedMarkets;
    $[25] = marketOptions;
    $[26] = t6;
  } else {
    marketOptions = $[25];
    t6 = $[26];
  }
  const channelOptions = t6;
  let t7;
  if ($[27] !== channelOptions || $[28] !== marketOptions) {
    t7 = [...marketOptions, ...channelOptions];
    $[27] = channelOptions;
    $[28] = marketOptions;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const breakdownOptions = t7;
  let t8;
  if ($[30] !== setUserSelectedChannels || $[31] !== setUserSelectedMarkets) {
    t8 = () => {
      setUserSelectedChannels([]);
      setUserSelectedMarkets([]);
    };
    $[30] = setUserSelectedChannels;
    $[31] = setUserSelectedMarkets;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const clearBreakdown = t8;
  let t9;
  if ($[33] !== isComparisonMode || $[34] !== setIsComparisonMode) {
    t9 = () => {
      setIsComparisonMode(!isComparisonMode);
    };
    $[33] = isComparisonMode;
    $[34] = setIsComparisonMode;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const handleComparisonMode = t9;
  let t10;
  if ($[36] !== selectedBreakdown.length || $[37] !== setIsComparisonMode) {
    t10 = () => {
      if (selectedBreakdown.length > 2) {
        setIsComparisonMode(false);
      }
    };
    $[36] = selectedBreakdown.length;
    $[37] = setIsComparisonMode;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== selectedBreakdown || $[40] !== setIsComparisonMode) {
    t11 = [selectedBreakdown, setIsComparisonMode];
    $[39] = selectedBreakdown;
    $[40] = setIsComparisonMode;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  useEffect(t10, t11);
  let t12;
  if ($[42] !== breakdownOptions || $[43] !== clearBreakdown || $[44] !== userSelectedChannels[0] || $[45] !== userSelectedChannels.length || $[46] !== userSelectedMarkets) {
    t12 = () => {
      if (userSelectedChannels.length > 0 && !breakdownOptions.some(option_0 => option_0.value === userSelectedChannels[0].value) || userSelectedMarkets.length > 0 && !breakdownOptions.some(option => option.value === userSelectedMarkets[0].value)) {
        clearBreakdown();
      }
    };
    $[42] = breakdownOptions;
    $[43] = clearBreakdown;
    $[44] = userSelectedChannels[0];
    $[45] = userSelectedChannels.length;
    $[46] = userSelectedMarkets;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== breakdownOptions || $[49] !== clearBreakdown || $[50] !== userSelectedChannels || $[51] !== userSelectedMarkets) {
    t13 = [userSelectedChannels, userSelectedMarkets, breakdownOptions, clearBreakdown];
    $[48] = breakdownOptions;
    $[49] = clearBreakdown;
    $[50] = userSelectedChannels;
    $[51] = userSelectedMarkets;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  useEffect(t12, t13);
  let t14;
  if ($[53] !== setUserSelectedMetrics || $[54] !== t) {
    t14 = (_, value_1) => {
      if (value_1.length < MINIMUM_METRICS) {
        makeErrorToast(t("filter.minimumOneMetricError"));
        return;
      }
      setUserSelectedMetrics(value_1.map(_temp3) as TMetric[]);
    };
    $[53] = setUserSelectedMetrics;
    $[54] = t;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  const handleMetricsChange = t14;
  let t15;
  if ($[56] !== finalizedMetrics || $[57] !== setUserSelectedMetrics || $[58] !== t || $[59] !== userSelectedMetrics.length) {
    t15 = () => {
      if (userSelectedMetrics.length > MAXIMUM_METRICS) {
        makeErrorToast(t("filter.maximumTwoMetricsError"));
        setUserSelectedMetrics(finalizedMetrics);
      }
    };
    $[56] = finalizedMetrics;
    $[57] = setUserSelectedMetrics;
    $[58] = t;
    $[59] = userSelectedMetrics.length;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  const handleMetricsDropdownClose = t15;
  let t16;
  if ($[61] !== clearBreakdown || $[62] !== setUserSelectedChannels || $[63] !== setUserSelectedMarkets || $[64] !== t) {
    t16 = (__0, value_2) => {
      const isMarketsSubCategory = last(value_2)?.subCategory === t("markets");
      if (value_2.length === 0) {
        clearBreakdown();
        return;
      }
      isMarketsSubCategory ? setUserSelectedMarkets(value_2) : setUserSelectedChannels(value_2);
    };
    $[61] = clearBreakdown;
    $[62] = setUserSelectedChannels;
    $[63] = setUserSelectedMarkets;
    $[64] = t;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  const handleBreakdownChange = t16;
  let t17;
  if ($[66] !== setAggregationSetting) {
    t17 = value_3 => {
      setAggregationSetting(value_3 as typeof aggregationSetting);
    };
    $[66] = setAggregationSetting;
    $[67] = t17;
  } else {
    t17 = $[67];
  }
  const handleAggregationSettingChange = t17;
  let t18;
  if ($[68] !== progressOverTime || $[69] !== timePeriod[0] || $[70] !== timePeriod[1]) {
    t18 = generateCompleteDateRange({
      startDate: timePeriod[0],
      endDate: timePeriod[1],
      lineChartData: progressOverTime,
      fillMissingLineChartData
    });
    $[68] = progressOverTime;
    $[69] = timePeriod[0];
    $[70] = timePeriod[1];
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  const progressOverTimeWithMissingDates = t18;
  const T0 = Block;
  const t19 = "0px";
  let t20;
  if ($[72] !== t) {
    t20 = t("progressOverTime.title");
    $[72] = t;
    $[73] = t20;
  } else {
    t20 = $[73];
  }
  let t21;
  if ($[74] !== t20) {
    t21 = <Typography variant="h3">{t20}</Typography>;
    $[74] = t20;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  const t22 = progressOverTime.length === 0 ? <StyledGrid container={true} direction="column" height="408px" alignItems="center" justifyContent="center"><ThemedImage src={toolsIcon} alt="Tools" width={40} height={40} />{errorBody ?? <Typography variant="h5">{t("noDataToDisplay")}</Typography>}</StyledGrid> : <><Grid container={true} marginTop={theme.spacing(4)} gap={theme.spacing(1)} alignItems="center"><Grid item={true}><Typography variant="body2">{t("showMe")}</Typography></Grid><Grid item={true}><MultiSelectDropdown title={t("buttons.selectMetrics")} buttonTitle={t("buttons.metrics")} allowSelectAll={false} value={userSelectedMetrics} options={metrics.map(metric => ({
          label: t(metric),
          value: metric,
          disabled: !userSelectedMetrics.includes(metric) && userSelectedMetrics.length >= MINIMUM_METRICS && (selectedBreakdown.length > MINIMUM_BREAKDOWN || userSelectedMetrics.length >= MAXIMUM_METRICS)
        }))} onChange={handleMetricsChange} onClose={handleMetricsDropdownClose} /></Grid><Grid item={true}><Tooltip kind="singleline" title="To be able to use the breakdown functionality, please select only 1 metric" disabled={finalizedMetrics.length === MINIMUM_METRICS}><Grid><MultiSelectDropdown title={t("buttons.selectBreakdown")} buttonTitle={t("buttons.breakdowns")} allowSelectAll={false} disabled={finalizedMetrics.length > 1} value={userSelectedBreakdown} options={breakdownOptions} onChange={handleBreakdownChange} /></Grid></Tooltip></Grid><Grid item={true}><SingleSelectDropdown title={t("progressOverTime.aggregationSetting.title")} buttonTitle={t("progressOverTime.aggregationSetting.title")} value={aggregationSetting} options={[{
          label: t("progressOverTime.aggregationSetting.dropDownDaily"),
          value: "daily"
        }, {
          label: t("progressOverTime.aggregationSetting.dropDownCumulative"),
          value: "cumulative"
        }]} onSelect={handleAggregationSettingChange} /></Grid>{selectedBreakdown.length > 0 && <StyledButton variant="text" scheme="light" onClick={clearBreakdown}><Typography variant="body2" color="link">{t("buttons.clearBreakdown")}</Typography></StyledButton>}</Grid><Divider /><Grid container={true} justifyContent="space-between" marginBottom={theme.spacing(2)} flexWrap="nowrap"><Grid item={true}><ChartLegend selectedRecords={getChartLegendData({
          progressOverTime,
          lineConfigs,
          selectedBreakdownCount: selectedBreakdown.length,
          selectedMetricsCount: finalizedMetrics.length,
          t,
          selectedPeriodType
        })} /></Grid><Grid item={true} flexWrap="nowrap">{selectedBreakdown.length <= 2 && <Tooltip kind="singleline" title={t("compareTooltip", {
          period: snakeCaseToTitleCase(selectedPeriodType)
        })}><Grid display="flex" flexWrap="nowrap"><StyledCompareToggleTypography variant="body2">{t("compareToggle")}</StyledCompareToggleTypography><Toggle aria-label={t("compareToggle")} name="comparsionMode" onChange={handleComparisonMode} checked={isComparisonMode} defaultChecked={true} /></Grid></Tooltip>}</Grid></Grid><Grid height="438px" width="100%"><LineChart data={progressOverTimeWithMissingDates} getConfigByDataKey={dataKey => {
        let lineConfig = {};
        const [metric_0] = getPartsFromDataKey(dataKey.toString());
        if (finalizedMetrics.length > 1 && metric_0 === finalizedMetrics[1]) {
          lineConfig = {
            yAxisId: "left"
          };
        }
        lineConfig = {
          ...lineConfig,
          ...lineConfigs[dataKey]
        };
        return lineConfig;
      }} renderTooltip={t23 => {
        const {
          payload
        } = t23;
        return <ChartToolTip width="fit-content" {...{
          customBody: <span>{selectedBreakdown.length <= 2 && isComparisonMode && <Typography variant="body3" color="inverse">{t("comparedToPreviousPeriod")}</Typography>}{Object.keys(payload).filter(_temp4).map(key_0 => {
              const [metric_1] = getPartsFromDataKey(key_0);
              return <div key={key_0} style={{
                width: "326px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}><div style={{
                  marginRight: "8px",
                  display: "flex",
                  alignItems: "center"
                }}><Badge color={lineConfigs[key_0].badgeColor} opacity={lineConfigs[key_0].lineOpacity} variant="lighter" /></div><div style={{
                  marginRight: "16px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>{formatTooltipDescriptionByDataKey({
                    dataKey: key_0,
                    payload,
                    timePeriod,
                    selectedBreakdownCount: selectedBreakdown.length,
                    selectedPeriodType,
                    t
                  })}</div><div style={{
                  marginLeft: "auto",
                  whiteSpace: "nowrap"
                }}>{getFormattedValue({
                    value: payload[key_0] as number,
                    id: metric_1,
                    currencyCode: targetCurrencyCode
                  })}</div></div>;
            })}</span>,
          title: payload.x.toString()
        }} />;
      }} margin={{
        left: 5,
        right: 5,
        top: 50
      }} xAxis={{
        tickLine: false
      }} yAxis={finalizedMetrics.map((metric_2, position) => ({
        label: {
          value: t(metric_2)
        },
        tick: {
          textAnchor: "middle"
        },
        ...(position === 1 && {
          yAxisId: "left",
          orientation: "left",
          label: {
            value: t(metric_2),
            dx: t(metric_2).length * Y_AXIS_LABEL_OFFSET
          }
        }),
        tickFormatter: value_4 => getFormattedValue({
          value: Number(value_4),
          id: metric_2,
          currencyCode: targetCurrencyCode,
          compact: true
        })
      }))} /></Grid></>;
  let t24;
  if ($[76] !== T0 || $[77] !== ref || $[78] !== t21 || $[79] !== t22) {
    t24 = <T0 marginTop={t19} ref={ref}>{t21}{t22}</T0>;
    $[76] = T0;
    $[77] = ref;
    $[78] = t21;
    $[79] = t22;
    $[80] = t24;
  } else {
    t24 = $[80];
  }
  return t24;
}
function _temp4(key) {
  return key !== "x" && key !== "fullDate";
}
function _temp3(item) {
  return item.value;
}
function _temp2(t0) {
  const {
    value: value_0
  } = t0;
  return value_0;
}
function _temp(t0) {
  const {
    value
  } = t0;
  return value;
}
const StyledGrid = styled(Grid)(({
  theme
}) => ({
  backgroundColor: theme.tokens.background.main.pressed,
  borderRadius: theme.borderRadius.medium,
  marginTop: theme.spacing(4),
  '& > img': {
    marginBottom: theme.spacing(3)
  }
}));
const StyledButton = styled(Button)(() => ({
  alignSelf: 'unset'
}));
const StyledCompareToggleTypography = styled(Typography)(() => ({
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  marginRight: '8px'
}));